import Cookies from 'js-cookie';
import scrollTo from '../../utils/scroll-to';
import ChartBase from '../../chart/chart-base';
import Plotlines from '../../chart/ui/plot-lines';
import { formatNumber } from '../../utils/intl';
import { default as PeriodSelector, EVENTS as periodSelectorEvents, TIMEFRAMES } from '../../chart/ui/period-selector';
import { default as ChartTypeSelector, EVENTS as chartTypeSelectorEvents } from '../../chart/ui/chart-type-selector';

const SERIES_ID = 'ReferencePrice';
const SERIES_CLASS_NAME = 'series-color-reference-price';

class ChartUnderlying extends ChartBase {
	constructor(el) {
		super(el);

		// Setup aditional interactive elements
		this.periodSelector = new PeriodSelector(this.refs.periodSelector);
		this.typeSelector = new ChartTypeSelector(this.refs.type);

		// Event listeners
		this.on('click');
		this.on(periodSelectorEvents.periodChange);
		this.on(chartTypeSelectorEvents.typeChange);
		this.eventbus.on('chart:plotline:toggle', data => this.onPlotlineToggle(data));
	}

	// Override
	afterRenderChart() {
		this.plotlines = new Plotlines(this.el, this.chart, 'y-axis-right');

		this.getChartData(this.endpoint);
	}

	// Override
	onGetChartDataSuccess(data) {
		this.addDataToChart(data);
		this.updateHighLow();
	}

	// Override
	onTick(tick) {
		super.onTick(tick);

		this.updateHighLow();
	}

	// Override
	hasQuotes(apiResponse = []) { // eslint-disable-line
		return apiResponse.length;
	}

	onClick(e) {
		const { target } = e;

		if (!target.classList.contains('js-plotline-remove')) {
			return;
		}

		const { id } = target.dataset;

		this.plotlines.remove(id);
	}

	onPeriodChange(e) {
		const { period } = e.detail;
		const isIntraday = period === TIMEFRAMES.intraday;
		const isDay = period === TIMEFRAMES.day;
		const useDateTime = isIntraday || isDay;
		const useOrdinal = !isIntraday;

		// The type selector needs to be disabled and set to line type in case of intraday period
		if (isIntraday) {
			this.typeSelector.setValue('line');
			this.typeSelector.disabled = true;
		} else {
			this.typeSelector.disabled = false;
		}

		// Some settings are period related so the chart config has to be
		this.updateChartConfig(useDateTime, useOrdinal, false);

		// Update chart
		this.getChartData(this.endpoint);

		// Only listen for live ticks when period is intraday
		this.toggleTickListener(isIntraday);
	}

	onTypeChange() {
		// Update chart
		this.getChartData(this.endpoint);
	}

	storeSettings() {
		const data = {
			activePeriod: this.periodSelector.activePeriod,
			activeChartType: this.typeSelector.activeChartType,
		};

		Cookies.set(this.settingsKey, JSON.stringify(data), { expires: 7 });
	}

	onPlotlineToggle({ isin, value }) {
		if (this.plotlines.exists(isin)) {
			this.plotlines.remove(isin);
		} else {
			const formattedValue = formatNumber(parseFloat(value));
			const text = `${isin}: stop loss @ ${formattedValue}`;

			this.plotlines.add(isin, value, text);

			scrollTo('.js-chart');
		}
	}

	addDataToChart(data) {
		const { chart } = this;
		const [series] = chart.series;
		const seriesConfig = {
			className: SERIES_CLASS_NAME,
			data,
			id: SERIES_ID,
			name: this.options.underlyingName,
			type: this.typeSelector.activeChartType,
			yAxis: 'y-axis-right',
		};

		if (series) {
			series.remove(false);
		}

		chart.addSeries(seriesConfig, true);

		if (this.plotlines.addedPlotlines.length) {
			this.plotlines.updateUI();
		}

		// Store settings in cookie
		this.storeSettings();
	}

	updateHighLow() {
		const yAxis = this.chart.get('y-axis-right');
		const { dataMin, dataMax } = yAxis.getExtremes();
		const highValue = typeof dataMin === 'number' ? formatNumber(dataMax) : '—';
		const lowValue = typeof dataMax === 'number' ? formatNumber(dataMin) : '—';

		this.refs.highValue.textContent = highValue;
		this.refs.lowValue.textContent = lowValue;
	}

	get endpoint() {
		const chartType = this.typeSelector.activeEndpointType;
		const { multiplier, period } = this.periodSelector.activePeriod;
		const { feedUrl, apiVersion, chartName, isin } = this.options;

		return `${feedUrl}/${apiVersion}/${chartName}/${chartType}/${isin}/${multiplier}/${period}`;
	}

	get storageKey() {
		return this.options.storageKey;
	}
}

export default ChartUnderlying;
